<script setup lang="ts">
import {useApiErrors} from "~/composables/useApiErrors"
import UpdateNotification from "~/components/UpdateNotification.vue"
import {useFirebaseApp} from "vuefire";
import {getMessaging} from "firebase/messaging";
import { useSnackbarStore } from '@/stores/SnackbarStore'

const {isAppLoaded} = useApp()
const {isAuthenticated} = useAuth()
const snackbar = useSnackbarStore()
const {errorMessage, errorState} = useApiErrors()
const firebaseApp = useFirebaseApp()
const messaging = getMessaging(firebaseApp)

// Ознака про необхідність оновити сторінку
const updateAvailable = ref(false)

// Отримуємо сповіщення про успішний деплой нової версії застосунку
useRuntimeHook('app:manifest:update', () => {
  updateAvailable.value = true
})

// Маніфест PWA — лише після входу: без нього браузер не пропонує встановити застосунок,
// тож зі сторінки логіну його не поставити. Маніфест генерує @vite-pwa/nuxt (див. nuxt.config).
useHead(() => ({
  link: isAuthenticated.value ? [{rel: 'manifest', href: '/manifest.webmanifest'}] : [],
}))

messaging.onMessageHandler = (payload) => {
  if (Notification.permission === 'granted') {
    new Notification(payload.notification.title, {
      body: payload.notification.body,
      icon: payload.notification.icon,
    });
  }
}
</script>

<template>
  <NuxtLoadingIndicator color="rgb(var(--v-theme-app-loader))" />
  <AppLoader v-if="!isAppLoaded"/>
  <NuxtLayout v-else>
    <NuxtPage/>

    <SnackError
        v-model="errorState"
        :text="errorMessage"
        timeout="10000" />

    <GlobalSnackbar
        :error-msg="snackbar.message"
        :snackbar-actions="snackbar.customActions"
        :snackbar-show="snackbar.isOpen"
        :snackbar-status="snackbar.status"
    />
  </NuxtLayout>

  <UpdateNotification v-model="updateAvailable" />
</template>

<style>
ul, ol {
  padding-left: 30px;
}

/* Копірайти карт: згорнуті в кружечок «ⓘ», розгортаються по кліку (див. useCompactAttribution).
   Стилі глобальні, бо контейнер атрибуції створює Leaflet поза Vue-деревом.
   Селектори починаються з .leaflet-container НЕ для краси: у самого Leaflet є
   `.leaflet-container .leaflet-control-attribution { margin: 0 }` тієї ж ваги, і його CSS
   вантажиться з компонентів ПІСЛЯ глобальних стилів — без цього префікса Leaflet перебиває
   наші відступи. */
.leaflet-container .leaflet-control-attribution.leaflet-attrib-compact {
  /* Leaflet саджає атрибуцію впритул до кутка — відсуваємо від країв мапи. */
  margin: 0 8px 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
  border-radius: 50%;
  cursor: pointer;
  /* Ховає текст саме у згорнутому стані: посилання всередині успадковують розмір. */
  font-size: 0;
}
.leaflet-container .leaflet-control-attribution.leaflet-attrib-compact::before {
  content: 'ⓘ';
  font-size: 14px;
  line-height: 1;
  color: #555;
}
.leaflet-container .leaflet-control-attribution.leaflet-attrib-compact.leaflet-attrib-compact--open {
  width: auto;
  max-width: min(92vw, 560px);
  height: auto;
  padding: 0 6px;
  border-radius: 3px;
  white-space: normal;
  font-size: 11px;
}
.leaflet-container .leaflet-control-attribution.leaflet-attrib-compact--open::before {
  content: none;
}
</style>